import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { OwnerSuspensionState } from '../services/api'
import { useStoreOwner } from '../store/useStoreOwner'
import { useOwnerBillingRevision } from '../lib/ownerBillingRefresh'

const NOT_SUSPENDED: OwnerSuspensionState = { suspended: false }

/**
 * 休会 (SaaS 利用料の未払い) の状態 (#1683)。
 *
 * オーナーは休会中も**ログインできる**ので、画面側でも「何が止まっているか」を
 * 知る必要がある。止めるのは客からの見え方・新規の受付・スタッフのログインだけで、
 * 支払い・売上の確認・予約の後始末はできる (規約 11.5 条)。
 *
 * 取得に失敗したときは休会していない扱いにする。バナーが出ないだけで操作は
 * 塞がない — サーバ側で止まっているものは止まっているので、ここを厳しくしても
 * 得るものがなく、通信エラーで店を驚かせるだけになる。
 *
 * uid と一緒に保持して、別オーナーへ切り替えた直後に前のオーナーの状態を
 * 出さないようにする (`RequireStoreAuth` の enrolledRecord と同じ形)。
 */
export function useOwnerSuspension(): OwnerSuspensionState {
  const isStoreLoggedIn = useStoreOwner((s) => s.isStoreLoggedIn)
  const uid = useStoreOwner((s) => s.firebaseUser?.uid)
  const rev = useOwnerBillingRevision()
  const [record, setRecord] = useState<{ uid: string; value: OwnerSuspensionState } | null>(null)

  useEffect(() => {
    if (!isStoreLoggedIn || !uid) return
    const targetUid = uid
    let cancelled = false
    api.getOwnerSuspension()
      .then((value) => { if (!cancelled) setRecord({ uid: targetUid, value }) })
      .catch(() => { if (!cancelled) setRecord({ uid: targetUid, value: NOT_SUSPENDED }) })
    return () => { cancelled = true }
  }, [isStoreLoggedIn, uid, rev])

  return record && uid && record.uid === uid ? record.value : NOT_SUSPENDED
}
